import { useEffect, useState } from 'react';
import ModalCentered from '../../ui/modal-window/ModalCentered';
import styles from '../../../pages/Mailing/MailingForm/mailingForm.module.sass';
import intStyles from './intersections.module.sass';
import { api } from '../../../services/api';
import Btn_transparent from '../../ui/btn_transparent/Btn_transparent';
import TextInput from '../../ui/input/TextInput';
import InfoTooltip from '../../ui/infoTooltip/infoTooltip';
import { fetchTasks } from '../../../store/task-slice/apiActions';
import { useAppDispatch } from '../../../hooks/redux';
import InfoBanner from '../../ui/infoBanner/infoBanner';
import Loader from '../../ui/loader/loader';
import { useTranslation } from 'react-i18next';

interface FileData {
  id: string;
  file: File;
  fileName: string;
  status: 'loading' | 'loaded' | 'error';
  errorMessage?: string;
}

const Intersections = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [taskName, setTaskName] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState<FileData[]>([]);
  const [rangeFrom, setRangeFrom] = useState(2);
  const [rangeTo, setRangeTo] = useState(2);
  const [rangeError, setRangeError] = useState('');

  const refreshTasks = () => {
    dispatch(fetchTasks({ limit: 10, offset: 0, operation: 'processing_db' }));
  };

  useEffect(() => {
    refreshTasks();
  }, []);

  const validateRange = (newFrom: number, newTo: number) => {
    if (newFrom > newTo) {
      setRangeError(t('usersFiltration.rangeFromCannotBeGreaterThanTo'));
      return false;
    }
    setRangeError('');
    return true;
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFrom = Number(e.target.value);
    if (validateRange(newFrom, rangeTo)) {
      setRangeFrom(newFrom);
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTo = Number(e.target.value);
    if (validateRange(rangeFrom, newTo)) {
      setRangeTo(newTo);
    }
  };
  const handleFilesChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const newFiles: FileData[] = Array.from(selectedFiles).map((file) => ({
      id: `${file.name}-${Date.now()}`,
      file,
      fileName: file.name,
      status: 'loading',
    }));

    setFiles((prev) => [...prev, ...newFiles.filter(Boolean)]);

    for (const fileData of newFiles) {
      try {
        const formData = new FormData();
        formData.append('file', fileData.file);
        const response = await api.post(
          '/telegram/processing/db/upload_file',
          formData,
        );

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id ? { ...f, status: 'loaded' } : f,
          ),
        );
      } catch (e: any) {
        const message =
          e.response?.data?.detail || t('usersFiltration.errorWhileUploading');
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? { ...f, status: 'error', errorMessage: message }
              : f,
          ),
        );
      }
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const submitHandler = async () => {
    const uploadedFiles = files.filter((f) => f.status === 'loaded');

    if (uploadedFiles.length < 2) {
      setError(t('usersFiltration.pleaseUploadAtLeast2Files'));
      return;
    }

    if (!taskName.trim()) {
      setError(t('usersFiltration.pleaseEnterTaskName'));
      return;
    }

    try {
      const requestData = {
        task_name: taskName.trim(),
        operation: 'intersection',
        db_files: uploadedFiles.map((f) => f.file.name),
        export_file_ext: uploadedFiles[0].file.name.split('.').pop() || 'xlsx',
        min_group_count: rangeFrom,
        max_group_count: rangeTo,
      };

      const response = await api.post('/telegram/processing/db/', requestData);

      if (response.status === 200) {
        setModalShow(true);
        refreshTasks();
        setFiles([]);
        setTaskName('');
      }
    } catch (e: any) {
      setError(
        e.response?.data?.detail || t('usersFiltration.errorSendingData'),
      );
    }
  };
  const loadedFilesCount = files.filter((f) => f.status === 'loaded').length;

  useEffect(() => {
    const loadedCount = files.filter((f) => f.status === 'loaded').length;

    if (loadedCount >= 2) {
      if (rangeTo !== loadedCount) {
        setRangeTo(loadedCount);
      }
      if (rangeFrom > loadedCount) {
        setRangeFrom(loadedCount - 1);
      }
    }
  }, [files]);

  return (
    <section className={intStyles.wrapper}>
      <ModalCentered
        show={modalShow}
        headerText={t('usersFiltration.intersectionSearchStarted')}
        bodyText={t('usersFiltration.parsingResultsInHistoryTable')}
        btnText={t('common.close')}
        onHide={() => setModalShow(false)}
      />

      <div className={`${intStyles.textWrapper} ${intStyles.outlinedBlock}`}>
        <span className={intStyles.textHeader}>
          <span className={`material-icons ${intStyles.icon}`}>search</span>
          {t('usersFiltration.functionIntersectingAudiences')}
        </span>
        <span>{t('usersFiltration.analyzeMembersComposition')}</span>
        <ul className={intStyles.textList}>
          <li className={intStyles.textItem}>
            <span className={`material-icons`}>checklist</span>
            {t('usersFiltration.supportsComparisonUpTo100Databases')}
          </li>
          <li className={intStyles.textItem}>
            <span className={`material-icons`}>upload_file</span>
            {t('usersFiltration.atLeast2DatabasesRequired')}
          </li>
        </ul>
        <p>{t('usersFiltration.toolHelpsIdentifyEngagedAudience')}</p>
      </div>

      <div className={`${intStyles.uploadBlock} ${intStyles.outlinedBlock}`}>
        <span className={intStyles.textWarning}>
          {t('usersFiltration.selectUserBasesHint')}
        </span>
        <div className={styles.uploadedFileInput}>
          <label htmlFor="multi-upload" className={intStyles.uploadLabel}>
            {t('usersFiltration.selectFiles')}
          </label>
          <input
            id="multi-upload"
            type="file"
            multiple
            onChange={handleFilesChange}
            className={intStyles.input}
          />
        </div>

        <div className={intStyles.filesList}>
          {files.filter(Boolean).map((f, index) => (
            <div key={f.id} className={intStyles.fileItem}>
              <div className={intStyles.fileItemBlock}>
                <div className={intStyles.fileNumber}>{index + 1}.</div>
                <div className={intStyles.fileName}>
                  {f?.fileName || t('usersFiltration.untitled')}
                </div>
              </div>
              <div className={intStyles.fileItemBlock}>
                {f.status === 'loading' && <Loader />}
                {f.status === 'loaded' && (
                  <div className={intStyles.success}>
                    {t('usersFiltration.fileUploaded')}
                  </div>
                )}
                {f.status === 'error' && (
                  <div className={intStyles.error}>{f.errorMessage}</div>
                )}
                <button
                  className={intStyles.removeFileButton}
                  onClick={() => removeFile(f.id)}
                  type="button"
                >
                  <span className="material-icons">cancel</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`${intStyles.rangeSection} ${intStyles.outlinedBlock}`}>
        <h3 className={intStyles.header}>
          {t('usersFiltration.numberOfIntersections')}
          <InfoTooltip
            text={t('usersFiltration.chooseNumberOfIntersections')}
          />
        </h3>
        <div className={intStyles.rangeBlock}>
          <span className={intStyles.text}>{t('usersFiltration.from')}</span>
          <select
            name="status"
            className={intStyles.select}
            value={rangeFrom}
            onChange={handleFromChange}
            disabled={loadedFilesCount < 2}
          >
            {Array.from({ length: loadedFilesCount - 1 }, (_, i) => i + 2).map(
              (val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ),
            )}
          </select>
          <span className={intStyles.text}>{t('usersFiltration.to')}</span>
          <select
            name="status"
            className={intStyles.select}
            value={rangeTo}
            onChange={handleToChange}
            disabled={loadedFilesCount < 2}
          >
            {Array.from({ length: loadedFilesCount - 1 }, (_, i) => i + 2).map(
              (val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ),
            )}
          </select>
        </div>
        {rangeError && <p className={intStyles.error}>{rangeError}</p>}
      </div>

      <div className={`${intStyles.fieldWrapper} ${intStyles.outlinedBlock}`}>
        <h3 className={intStyles.header}>
          {t('common.taskName')}{' '}
          <InfoTooltip
            text={t('usersFiltration.giveIntersectionSearchTaskName1')}
          />
        </h3>

        <TextInput
          style={{ maxWidth: '610px' }}
          name="name"
          type="text"
          placeholder={t('usersFiltration.giveIntersectionSearchTaskName2')}
          hintMessage={t('usersFiltration.nameVisibleOnlyToYou')}
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
        />
      </div>

      <div className={intStyles.submitSection}>
        {error && <p className={intStyles.error}>{error}</p>}
        <Btn_transparent
          className={intStyles.submitButton}
          variant="standart"
          type="button"
          onClick={submitHandler}
        >
          {t('usersFiltration.startSearchingForIntersections')}
        </Btn_transparent>
      </div>
    </section>
  );
};

export default Intersections;
